import { createContext, useContext, useId, type ReactNode } from "react";

import { cn } from "../lib/cn";

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error(`<${component}> has to be rendered inside <Tabs>.`);
  }
  return context;
}

function idPart(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]+/g, "-");
}

function tabId(baseId: string, value: string): string {
  return `${baseId}-tab-${idPart(value)}`;
}

function panelId(baseId: string, value: string): string {
  return `${baseId}-panel-${idPart(value)}`;
}

export interface TabsProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/** Controlled tab root: shares the selected value and id namespace with its parts. */
export function Tabs({
  value,
  onValueChange,
  children,
  className,
  ...rest
}: TabsProps) {
  const baseId = useId();

  return (
    <TabsContext.Provider value={{ value, onValueChange, baseId }}>
      <div {...rest} className={cn("flex flex-col", className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabListProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "role" | "onKeyDown"
> {
  children: ReactNode;
  "aria-label"?: string;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/** The tab row; owns APG arrow-key navigation. */
export function TabList({
  children,
  "aria-label": ariaLabel,
  className,
  ...rest
}: TabListProps) {
  const { onValueChange } = useTabsContext("TabList");

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "Home" &&
      event.key !== "End"
    ) {
      return;
    }

    const tabs = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]:not([disabled])',
      ),
    );
    if (tabs.length === 0) return;

    const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex: number;
    switch (event.key) {
      case "ArrowLeft":
        nextIndex = current <= 0 ? tabs.length - 1 : current - 1;
        break;
      case "ArrowRight":
        nextIndex = current === tabs.length - 1 ? 0 : current + 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      default:
        nextIndex = tabs.length - 1;
    }

    const next = tabs[nextIndex];
    if (!next) return;

    event.preventDefault();
    next.focus();
    // Activation follows focus (the panels here are cheap to render).
    if (next.dataset.value !== undefined) {
      onValueChange(next.dataset.value);
    }
  }

  return (
    <div
      {...rest}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn(
        "flex items-stretch gap-1 overflow-x-auto shadow-[inset_0_-1px_0_var(--color-rule)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface TabProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "role" | "type" | "value" | "onClick"
> {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  ref?: React.Ref<HTMLButtonElement>;
}

/** Inset ring: the scrolling list would clip an outset one. */
export function Tab({
  value,
  children,
  disabled = false,
  className,
  ...rest
}: TabProps) {
  const { value: selected, onValueChange, baseId } = useTabsContext("Tab");
  const active = selected === value;

  return (
    <button
      {...rest}
      type="button"
      role="tab"
      id={tabId(baseId, value)}
      data-value={value}
      aria-selected={active}
      aria-controls={panelId(baseId, value)}
      tabIndex={active ? 0 : -1}
      disabled={disabled}
      onClick={() => onValueChange(value)}
      className={cn(
        "relative shrink-0 cursor-pointer rounded-paper px-3 py-3",
        "whitespace-nowrap font-body text-sm font-medium",
        "transition-colors duration-150 ease-paper",
        "after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full",
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
        "disabled:cursor-not-allowed disabled:text-faint",
        active ? "text-ink after:bg-ink" : "text-annotation hover:text-ink",
        className,
      )}
    >
      {children}
    </button>
  );
}

export interface TabPanelProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "role" | "hidden"
> {
  value: string;
  children: ReactNode;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

export function TabPanel({
  value,
  children,
  className,
  ...rest
}: TabPanelProps) {
  const { value: selected, baseId } = useTabsContext("TabPanel");
  const active = selected === value;

  return (
    <div
      {...rest}
      role="tabpanel"
      id={panelId(baseId, value)}
      aria-labelledby={tabId(baseId, value)}
      hidden={!active}
      tabIndex={0}
      className={cn(
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
        className,
      )}
    >
      {children}
    </div>
  );
}
